import groupBy from 'lodash/groupBy';

/**
 * CM charge constructor model, shared by CmChargeBlock (single user) and the mass-charge wizard.
 * Entry shape: {id, type, chargeType, category, key, selectedId?, amount, date, customFields, customKey}
 */

export const newCmEntry = () => ({
    id: Math.random(),
    type: null,
    chargeType: null,
    category: null,
    key: null,
    amount: null,
    date: null,
    customFields: null,
    customKey: ""
});

const isManualKey = (entry) => entry?.key === "" || entry?.key === "0";
const findChargeType = (entry, chargeTypes) =>
    (entry?.selectedId && chargeTypes.find(i => i.id === entry.selectedId)) ||
    (entry?.key && chargeTypes.find(i => i.key === entry.key && i.category === entry.category)) ||
    (entry?.key && chargeTypes.find(i => i.key === entry.key)) ||
    null;

// One field change of the constructor (SelectedItemBlock.handleChange semantics):
// any change resets amount, type/category changes reset the dependent fields, key picks the chargeType.
export function updateCmItem(item, fieldName, value, chargeTypes) {
    const current = {...item, amount: null};
    current[fieldName] = value;

    if (fieldName === "key") {
        if (current.selectedId) {
            const exactType = chargeTypes.find(type => type.id === current.selectedId);
            if (exactType) current.chargeType = exactType.chargeType;
        } else {
            const type = chargeTypes.filter(type => type.key == value);
            if (type.length == 1) current.chargeType = type[0].chargeType;
        }
    }
    if (fieldName === "type") {
        current.category = null;
        current.key = null;
        current.chargeType = null;
    }
    if (fieldName === "category") {
        current.key = null;
        current.chargeType = null;
    }
    return current;
}

// selectedItemList → chargeData of the CM charge endpoint
export function buildChargeDataCM(selectedItemList) {
    const groupedBy = groupBy(selectedItemList, (i) => i.type);
    const chargeData = {consumables: [], actions: []};

    const withType = (charge, i) => {
        if (i.chargeType != undefined && i.chargeType != null && i.chargeType != '') {
            charge['type'] = i.chargeType.trim();
        }
        return charge;
    };

    groupedBy.items && groupedBy.items.forEach(i => {
        const idToUse = isManualKey(i) ? i.customKey : i.key;
        chargeData.consumables.push(withType({
            id: String(idToUse ?? "").trim(),
            value: i.amount ? i.amount : i.date
        }, i));
    });
    groupedBy.actions && groupedBy.actions.forEach(i => {
        const idToUse = isManualKey(i) ? i.customKey : i.key;
        chargeData.actions.push(withType({
            id: String(idToUse ?? "").trim(),
            value: i.customFields
        }, i));
    });
    return chargeData;
}

// Adapter for MassChargeWizard
export function createCmAdapter(chargeTypes = []) {
    const hasItem = (entry) => Boolean(entry?.type && (isManualKey(entry) ? entry.customKey : entry.key));
    return {
        newEntry: newCmEntry,
        hasItem,
        label: (entry) => {
            if (!hasItem(entry)) return "";
            const chargeType = findChargeType(entry, chargeTypes);
            const title = chargeType?.title || entry.customKey || entry.key;
            return entry.category ? `${entry.category}: ${title}` : title;
        },
        id: (entry) => (isManualKey(entry) ? entry?.customKey : entry?.key) || "",
        getAmount: (entry) => entry?.amount ?? null,
        setAmount: (entry, amount) => ({...entry, amount}),
        // itemName → entry by charge type title or key (case-insensitive)
        resolve: (itemName) => {
            if (!itemName) return null;
            const needle = itemName.toLowerCase();
            const chargeType = chargeTypes.find(ct => ct.title?.toLowerCase() === needle || ct.key?.toLowerCase() === needle);
            if (!chargeType) return null;
            return {
                ...newCmEntry(),
                type: chargeType.type,
                chargeType: chargeType.chargeType,
                category: chargeType.category,
                key: chargeType.key,
                selectedId: chargeType.id
            };
        }
    };
}
